import React, { useState, useContext } from 'react';
import {
  Button,
  Modal,
  Typography,
  Input
} from 'antd';
import { CaretRightOutlined, CloseCircleOutlined } from '@ant-design/icons';
import StatusMessages, { StatusMessage } from '../../components/StatusMessages';
import { ThemeContext, ThemeContextState } from '../../context/ThemeContext';
import styles from './ProblemTestcaseModal.module.css';

export interface ProblemTestcaseModalProps {
  isOpen: boolean
  isLoading: boolean
  handleRun: (e: React.MouseEvent<HTMLElement>, testcase: any[]) => void
  handleClose: (e: React.MouseEvent<HTMLElement>) => void
};

// modal to display problem custom testcase results
const ProblemTestcaseModal = (props: ProblemTestcaseModalProps): JSX.Element | null => {
  // custom testcase in string format
  const [testcase, setTestcase] = useState<string>('');
  // modal status messages
  const [messages, setMessages] = useState<StatusMessage[]>([]);
  // get current theme from theme context
  const { theme } = useContext(ThemeContext) as ThemeContextState;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    setTestcase(e.target.value);
  };

  const handleRunWrapper = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
  
    // clear status messages
    setMessages((previousMessages: StatusMessage[]) => []);

    // testcase must be array of arguments
    // attempt to parse array of arguments
    let testcaseArgs: any[];
    try {
      testcaseArgs = JSON.parse(testcase);
    } catch(error: any) {
      // set error status messages on parsing failure
      setMessages((previousMessages: StatusMessage[]) => [{
        message: 'Invalid testcase',
        status: 'error'
      }]);
      return;
    }

    if (!Array.isArray(testcaseArgs)) {
      // set error status messages if parsed testcase is not an array
      setMessages((previousMessages: StatusMessage[]) => [{
        message: 'Invalid testcase',
        status: 'error'
      }]);
      return;
    }

    props.handleRun(e, testcaseArgs);
  };

  const handleCloseWrapper = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    props.handleClose(e);
  };

  return (
    <Modal
      className={theme === 'light' ? 'problemModalLight' : 'problemModalDark'}
      title={
        <Typography.Text
          className={`${styles.text} ${theme === 'light' ? styles.textLight : styles.textDark}`}
        >
          Custom Testcase
        </Typography.Text>
      }
      open={props.isOpen}
      closable={false}
      footer={[
        <Button
          key='close'
          onClick={handleCloseWrapper}
          type='primary'
          className='secondaryButtonDark'
        >
          <CloseCircleOutlined />
          Close
        </Button>,
        <Button
          key='run'
          onClick={handleRunWrapper}
          type='primary'
          className='mainButton'
          loading={props.isLoading}
        >
          <CaretRightOutlined />
          Run
        </Button>
      ]}
    >
      <StatusMessages items={messages} />
      <Input.TextArea
        placeholder='[arg1, arg2, ...]'
        rows={3}
        onChange={handleChange}
      />
    </Modal>
  );
};

export default ProblemTestcaseModal;
